import { useState, useEffect } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { 
  Key, 
  Building2, 
  Plus, 
  MoreVertical, 
  RefreshCw,
  Users,
  Calendar,
  AlertTriangle,
  CheckCircle
} from "lucide-react";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("fr-FR").format(amount) + " FCFA";
};

const licenceTypes = {
  essai: { label: "Essai", className: "bg-zinc-800 text-zinc-300 border-zinc-700" },
  standard: { label: "Standard", className: "bg-blue-900/50 text-blue-300 border-blue-800" },
  premium: { label: "Premium", className: "bg-amber-900/50 text-amber-300 border-amber-800" },
  enterprise: { label: "Enterprise", className: "bg-emerald-900/50 text-emerald-300 border-emerald-800" },
};

export default function Licences() {
  const [tenants, setTenants] = useState([]);
  const [licences, setLicences] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState("tenant");
  const [formData, setFormData] = useState({});

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const roleRes = await apiClient.get("/users/me/role");
      setUserRole(roleRes.data);
      
      if (roleRes.data.is_super_admin) {
        await loadData();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const [tenantsRes, licencesRes] = await Promise.all([
        apiClient.get("/tenants"),
        apiClient.get("/licences"),
      ]);
      setTenants(tenantsRes.data);
      setLicences(licencesRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/tenants", formData);
      toast.success("Client créé avec succès");
      setDialogOpen(false);
      setFormData({});
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    }
  };

  const handleCreateLicence = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/licences", {
        ...formData,
        duree_mois: parseInt(formData.duree_mois || 12),
        max_utilisateurs: parseInt(formData.max_utilisateurs || 5),
        montant: parseFloat(formData.montant || 0),
      });
      toast.success("Licence créée avec succès");
      setDialogOpen(false);
      setFormData({});
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    }
  };

  const handleRenewLicence = async (licenceId) => {
    try {
      await apiClient.put(`/licences/${licenceId}/renouveler?duree_mois=12`);
      toast.success("Licence renouvelée pour 12 mois");
      loadData();
    } catch (error) {
      toast.error("Erreur lors du renouvellement");
    }
  };

  const isLicenceExpired = (dateFin) => {
    return new Date(dateFin) < new Date();
  };

  const isLicenceExpiringSoon = (dateFin) => {
    const daysLeft = Math.ceil((new Date(dateFin) - new Date()) / (1000 * 60 * 60 * 24));
    return daysLeft > 0 && daysLeft <= 30;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-400">Chargement...</div>
      </div>
    );
  }

  if (!userRole?.is_super_admin) {
    return (
      <div className="space-y-6" data-testid="licences-page">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white tracking-tight">Gestion des Licences</h1>
          <p className="text-zinc-500 mt-1">Administration multi-clients</p>
        </div>
        <Card className="bg-[#121214] border-zinc-800">
          <CardContent className="py-12 text-center">
            <Key className="w-12 h-12 mx-auto text-zinc-700 mb-4" />
            <p className="text-zinc-400 font-medium">Accès réservé aux Super Administrateurs</p>
            <p className="text-zinc-500 text-sm mt-1">
              Contactez IDEAL Consulting pour accéder à cette fonctionnalité
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="licences-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white tracking-tight">Gestion des Licences</h1>
          <p className="text-zinc-500 mt-1">Administration multi-clients</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            className="border-zinc-700"
            onClick={() => { setDialogType("tenant"); setDialogOpen(true); }}
          >
            <Building2 className="w-4 h-4 mr-2" />
            Nouveau Client
          </Button>
          <Button 
            className="bg-blue-600 hover:bg-blue-700"
            onClick={() => { setDialogType("licence"); setDialogOpen(true); }}
          >
            <Key className="w-4 h-4 mr-2" />
            Nouvelle Licence
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-[#121214] border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-zinc-500">Clients</p>
                <p className="text-3xl font-heading font-bold text-white">{tenants.length}</p>
              </div>
              <Building2 className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#121214] border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-zinc-500">Licences actives</p>
                <p className="text-3xl font-heading font-bold text-emerald-400">
                  {licences.filter(l => l.is_active && !isLicenceExpired(l.date_fin)).length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#121214] border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-zinc-500">Expirent bientôt</p>
                <p className="text-3xl font-heading font-bold text-amber-400">
                  {licences.filter(l => isLicenceExpiringSoon(l.date_fin)).length}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#121214] border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-zinc-500">Utilisateurs</p>
                <p className="text-3xl font-heading font-bold text-white">
                  {tenants.reduce((sum, t) => sum + (t.users_count || 0), 0)}
                </p>
              </div>
              <Users className="w-8 h-8 text-violet-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tenants List */}
      <Card className="bg-[#121214] border-zinc-800">
        <CardHeader>
          <CardTitle className="font-heading text-white">Clients & Licences</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {tenants.length === 0 ? (
              <p className="text-zinc-500 text-center py-8">Aucun client enregistré</p>
            ) : (
              tenants.map((tenant) => {
                const licence = tenant.licence;
                const expired = licence && isLicenceExpired(licence.date_fin);
                const expiringSoon = licence && isLicenceExpiringSoon(licence.date_fin);
                
                return (
                  <div 
                    key={tenant.id} 
                    className={`p-4 rounded-lg border ${
                      expired ? 'border-red-800 bg-red-900/10' :
                      expiringSoon ? 'border-amber-800 bg-amber-900/10' :
                      'border-zinc-800 bg-zinc-900/50'
                    }`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-heading font-semibold text-white">{tenant.nom_entreprise}</h3>
                          {licence && (
                            <Badge variant="outline" className={licenceTypes[licence.type_licence]?.className}>
                              {licenceTypes[licence.type_licence]?.label}
                            </Badge>
                          )}
                          {expired && (
                            <Badge className="bg-red-900/50 text-red-300">Expirée</Badge>
                          )}
                          {expiringSoon && (
                            <Badge className="bg-amber-900/50 text-amber-300">Expire bientôt</Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-zinc-500">Email</p>
                            <p className="text-zinc-300">{tenant.email_contact}</p>
                          </div>
                          <div>
                            <p className="text-zinc-500">Utilisateurs</p>
                            <p className="text-zinc-300">{tenant.users_count || 0} / {licence?.max_utilisateurs || "-"}</p>
                          </div>
                          <div>
                            <p className="text-zinc-500">NIF</p>
                            <p className="text-zinc-300 font-mono">{tenant.nif || "-"}</p>
                          </div>
                          <div>
                            <p className="text-zinc-500">Expiration</p>
                            <p className={`font-mono ${expired ? 'text-red-400' : expiringSoon ? 'text-amber-400' : 'text-zinc-300'}`}>
                              {licence ? new Date(licence.date_fin).toLocaleDateString('fr-FR') : "-"}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        {licence && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRenewLicence(licence.id)}
                            className="border-emerald-700 text-emerald-400 hover:bg-emerald-900/30"
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Renouveler
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setFormData({ tenant_id: tenant.id });
                            setDialogType("licence");
                            setDialogOpen(true);
                          }}
                          className="border-blue-700 text-blue-400 hover:bg-blue-900/30"
                        >
                          <Key className="w-4 h-4 mr-1" />
                          Licence
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#121214] border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">
              {dialogType === "tenant" ? "Nouveau Client" : "Nouvelle Licence"}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              {dialogType === "tenant" 
                ? "Enregistrez un nouveau client pour la plateforme" 
                : "Créez ou renouvelez une licence"}
            </DialogDescription>
          </DialogHeader>
          
          {dialogType === "tenant" ? (
            <form onSubmit={handleCreateTenant} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Nom de l'entreprise *</Label>
                <Input
                  value={formData.nom_entreprise || ""}
                  onChange={(e) => setFormData({...formData, nom_entreprise: e.target.value})}
                  required
                  className="bg-zinc-900/50 border-zinc-800"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Email contact *</Label>
                <Input
                  type="email"
                  value={formData.email_contact || ""}
                  onChange={(e) => setFormData({...formData, email_contact: e.target.value})}
                  required
                  className="bg-zinc-900/50 border-zinc-800"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">NIF</Label>
                  <Input
                    value={formData.nif || ""}
                    onChange={(e) => setFormData({...formData, nif: e.target.value})}
                    className="bg-zinc-900/50 border-zinc-800 font-mono"
                    placeholder="Numéro fiscal"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">RCCM</Label>
                  <Input
                    value={formData.rccm || ""}
                    onChange={(e) => setFormData({...formData, rccm: e.target.value})}
                    className="bg-zinc-900/50 border-zinc-800 font-mono"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Téléphone</Label>
                <Input
                  value={formData.telephone || ""}
                  onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                  className="bg-zinc-900/50 border-zinc-800"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700">
                  Annuler
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  Créer
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleCreateLicence} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Client *</Label>
                <Select
                  value={formData.tenant_id || ""}
                  onValueChange={(v) => setFormData({...formData, tenant_id: v})}
                  required
                >
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-800">
                    <SelectValue placeholder="Sélectionner un client" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-zinc-800">
                    {tenants.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.nom_entreprise}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Type de licence</Label>
                <Select
                  value={formData.type_licence || "standard"}
                  onValueChange={(v) => setFormData({...formData, type_licence: v})}
                >
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-zinc-800">
                    <SelectItem value="essai">Essai (30 jours)</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Durée (mois)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.duree_mois || 12}
                    onChange={(e) => setFormData({...formData, duree_mois: e.target.value})}
                    className="bg-zinc-900/50 border-zinc-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Max utilisateurs</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.max_utilisateurs || 5}
                    onChange={(e) => setFormData({...formData, max_utilisateurs: e.target.value})}
                    className="bg-zinc-900/50 border-zinc-800"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">Montant (FCFA)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.montant || ""}
                  onChange={(e) => setFormData({...formData, montant: e.target.value})}
                  className="bg-zinc-900/50 border-zinc-800"
                  placeholder="0"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700">
                  Annuler
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  Créer Licence
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
